/**
 * Add a person, and add an organization.
 *
 * ONE CALL, NOT SIX. The service creates the contact, its email and phone rows, the organization
 * and the relationship profile together. Doing that from here would make "contact exists but has no
 * relationship profile" reachable — a record that is in the database and invisible to every screen.
 *
 * THE DUPLICATE RESPONSE IS ACTIONABLE. A 409 comes back naming the existing contact and carrying
 * its id, so this offers to OPEN them. Being told "duplicate" and left to search for the record you
 * have just been told about is the version of this that wastes the most time.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { tfApi } from '../../api/tf_api'
import { useApp } from '../../contexts/app_context'
import { ModalShell, addDaysIso } from './primitives'
import type { NewPerson } from '../../types/tf_types'

interface DuplicateInfo {
  contactId: number
  displayName: string
  message: string
}

export function NewPersonModal({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (contactId: number) => void
}) {
  const { options, config, notify, notifyError } = useApp()
  const navigate = useNavigate()

  const [form, setForm] = useState<NewPerson>({
    FirstName: '',
    LastName: '',
    PrimaryEmail: '',
    PrimaryPhone: '',
    JobTitle: '',
    OrganizationName: '',
    LinkedInPage: '',
    RelationshipStrength: '',
    ContactStatus: '',
    NextMove: '',
    NextFollowUpDate: '',
    OwnerUserId: config?.defaultOwner ?? '',
    RelationshipTypes: [],
  })
  const [busy, setBusy] = useState(false)
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null)

  const set = (field: keyof NewPerson) => (value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
    if (field === 'PrimaryEmail') setDuplicate(null)
  }

  const toggleType = (value: string) => {
    setForm((f) => {
      const current = f.RelationshipTypes ?? []
      return {
        ...f,
        RelationshipTypes: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      }
    })
  }

  //  The service refuses a record with no name and no email. Mirroring that here means the user
  //  sees it before they submit rather than as a rejection afterwards.
  const identifiable = Boolean(
    (form.FirstName || '').trim() || (form.LastName || '').trim() || (form.PrimaryEmail || '').trim(),
  )

  const save = async () => {
    setBusy(true)
    setDuplicate(null)
    try {
      const payload: NewPerson = Object.fromEntries(
        Object.entries(form).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '',
        ),
      )
      const created = await tfApi.createPerson(payload)
      notify({
        tone: 'ok',
        title: 'Person added',
        body: `${created.DisplayName} is now in your relationships.`,
      })
      onCreated(created.ContactId)
      onClose()
    } catch (err: any) {
      const detail = err?.detail?.detail ?? err?.detail
      if (err?.status === 409 && detail?.ContactId) {
        setDuplicate({
          contactId: Number(detail.ContactId),
          displayName: String(detail.DisplayName ?? 'That contact'),
          message: String(detail.message ?? 'Somebody already has that email.'),
        })
      } else {
        notifyError('Could not add this person', err)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title="Add a person"
      description="Enough to find them again is enough to start. A name or an email will do — everything else can be filled in from the profile."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn primary"
            type="button"
            disabled={busy || !identifiable}
            onClick={() => void save()}
          >
            {busy ? 'Adding…' : 'Add person'}
          </button>
        </>
      }
    >
      {duplicate && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          <span className="material-symbols-outlined">person_search</span>
          <div>
            <b>{duplicate.message}</b>
            <div style={{ marginTop: 4 }}>
              Adding them again would split their history across two records, so neither would
              answer “when did we last speak”.
            </div>
            <button
              className="btn secondary sm"
              type="button"
              style={{ marginTop: 8 }}
              onClick={() => {
                navigate(`/relationships/people/${duplicate.contactId}`)
                onClose()
              }}
            >
              <span className="material-symbols-outlined">open_in_new</span>
              Open {duplicate.displayName}
            </button>
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="form-label" htmlFor="np-first">First name</label>
          <input id="np-first" className="form-input" value={form.FirstName}
                 onChange={(e) => set('FirstName')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="np-last">Last name</label>
          <input id="np-last" className="form-input" value={form.LastName}
                 onChange={(e) => set('LastName')(e.target.value)} />
        </div>

        <div className="field">
          <label className="form-label" htmlFor="np-email">Email</label>
          <input id="np-email" className="form-input" type="email" value={form.PrimaryEmail}
                 onChange={(e) => set('PrimaryEmail')(e.target.value)} />
          <span className="form-hint">The field duplicates are checked against.</span>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="np-phone">Phone</label>
          <input id="np-phone" className="form-input" value={form.PrimaryPhone}
                 onChange={(e) => set('PrimaryPhone')(e.target.value)} />
        </div>

        <div className="field">
          <label className="form-label" htmlFor="np-org">Organization</label>
          <input id="np-org" className="form-input" value={form.OrganizationName}
                 onChange={(e) => set('OrganizationName')(e.target.value)} />
          <span className="form-hint">Matched to an existing account, or created.</span>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="np-title">Job title</label>
          <input id="np-title" className="form-input" value={form.JobTitle}
                 onChange={(e) => set('JobTitle')(e.target.value)} />
        </div>

        <div className="field span-2">
          <label className="form-label" htmlFor="np-linkedin">LinkedIn page</label>
          <input id="np-linkedin" className="form-input" value={form.LinkedInPage}
                 onChange={(e) => set('LinkedInPage')(e.target.value)} />
        </div>

        <div className="field span-2">
          <span className="form-label">How do you know them?</span>
          <div className="chip-row" style={{ marginTop: 4 }}>
            {options('RELATIONSHIP_TYPE').map((v) => (
              <button
                key={v.ValueCode}
                type="button"
                className={`chip${(form.RelationshipTypes ?? []).includes(v.ValueLabel) ? ' on' : ''}`}
                onClick={() => toggleType(v.ValueLabel)}
              >
                {v.ValueLabel}
              </button>
            ))}
          </div>
          {/* A person can be several of these at once — a candidate today and a client contact in
              two years — so this is a multi-select rather than a dropdown. */}
          <span className="form-hint">Pick as many as apply.</span>
        </div>

        <div className="field">
          <label className="form-label" htmlFor="np-strength">Relationship strength</label>
          <select id="np-strength" className="form-input" value={form.RelationshipStrength}
                  onChange={(e) => set('RelationshipStrength')(e.target.value)}>
            <option value="">— not set —</option>
            {options('RELATIONSHIP_STRENGTH').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="np-status">Status</label>
          <select id="np-status" className="form-input" value={form.ContactStatus}
                  onChange={(e) => set('ContactStatus')(e.target.value)}>
            <option value="">— not set —</option>
            {options('CONTACT_STATUS').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <label className="form-label" htmlFor="np-move">Next move</label>
          <input id="np-move" className="form-input" value={form.NextMove}
                 onChange={(e) => set('NextMove')(e.target.value)}
                 placeholder="Send the intro email" />
        </div>

        <div className="field">
          <label className="form-label" htmlFor="np-date">Follow-up date</label>
          <input id="np-date" className="form-input" type="date" value={form.NextFollowUpDate ?? ''}
                 onChange={(e) => set('NextFollowUpDate')(e.target.value)} />
          {/* Setting the date creates the follow-up in the same call. Without it the next move is
              a note: nothing puts it in the daily queue, so nothing makes it happen. */}
          <span className="form-hint">
            Creates the follow-up.{' '}
            <button className="btn ghost sm" type="button"
                    onClick={() => set('NextFollowUpDate')(addDaysIso(7))}>
              in a week
            </button>
          </span>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="np-owner">Owner</label>
          <input id="np-owner" className="form-input" value={form.OwnerUserId}
                 onChange={(e) => set('OwnerUserId')(e.target.value)} />
        </div>
      </div>

      {!identifiable && (
        <p className="person-sub" style={{ marginTop: 10 }}>
          A name or an email is needed — without one there is nothing here to identify a person by.
        </p>
      )}
    </ModalShell>
  )
}

/**
 * Add an organization.
 *
 * Creates the company AND its Tidalfin profile. Without the profile the account exists but has
 * nowhere to record what happens next, which is the only reason Tidalfin tracks accounts separately
 * from the people in them.
 */
export function NewOrganizationModal({ onClose, onCreated }: {
  onClose: () => void
  onCreated: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const [form, setForm] = useState({
    CompanyName: '', Website: '', Industry: '', Headquarters: '',
    CompanySize: '', StrategicValue: '', NextMove: '', NextFollowUpDate: '',
  })
  const [busy, setBusy] = useState(false)
  const [duplicate, setDuplicate] = useState<string | null>(null)

  const set = (field: keyof typeof form) => (value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
    if (field === 'CompanyName') setDuplicate(null)
  }

  const save = async () => {
    setBusy(true)
    setDuplicate(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v ?? '').trim() !== ''),
      ) as { CompanyName: string }
      await tfApi.createOrganization(payload)
      notify({ tone: 'ok', title: 'Organization added', body: form.CompanyName })
      onCreated()
      onClose()
    } catch (err: any) {
      const detail = err?.detail?.detail ?? err?.detail
      if (err?.status === 409) {
        setDuplicate(String(detail?.message ?? 'That organization is already here.'))
      } else {
        notifyError('Could not add this organization', err)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title="Add an organization"
      description="An account, with somewhere to record what happens next."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button"
                  disabled={busy || !form.CompanyName.trim()} onClick={() => void save()}>
            {busy ? 'Adding…' : 'Add organization'}
          </button>
        </>
      }
    >
      {duplicate && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          <span className="material-symbols-outlined">domain_disabled</span>
          <div>
            <b>{duplicate}</b>
            <div style={{ marginTop: 4 }}>
              “Acme Inc.” and “Acme” are matched as the same account, so contacts do not end up
              split across two versions of one company.
            </div>
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="form-label" htmlFor="no-name">Organization name</label>
          <input id="no-name" className="form-input" value={form.CompanyName}
                 onChange={(e) => set('CompanyName')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-web">Website</label>
          <input id="no-web" className="form-input" value={form.Website}
                 onChange={(e) => set('Website')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-industry">Industry</label>
          <select id="no-industry" className="form-input" value={form.Industry}
                  onChange={(e) => set('Industry')(e.target.value)}>
            <option value="">— not set —</option>
            {options('INDUSTRY').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-hq">Headquarters</label>
          <input id="no-hq" className="form-input" value={form.Headquarters}
                 onChange={(e) => set('Headquarters')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-size">Company size</label>
          <select id="no-size" className="form-input" value={form.CompanySize}
                  onChange={(e) => set('CompanySize')(e.target.value)}>
            <option value="">— not set —</option>
            {options('COMPANY_SIZE').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-value">Strategic value</label>
          <select id="no-value" className="form-input" value={form.StrategicValue}
                  onChange={(e) => set('StrategicValue')(e.target.value)}>
            <option value="">— not set —</option>
            {options('STRATEGIC_VALUE').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="no-date">Follow-up date</label>
          <input id="no-date" className="form-input" type="date" value={form.NextFollowUpDate}
                 onChange={(e) => set('NextFollowUpDate')(e.target.value)} />
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="no-move">Next move</label>
          <input id="no-move" className="form-input" value={form.NextMove}
                 onChange={(e) => set('NextMove')(e.target.value)} />
        </div>
      </div>
    </ModalShell>
  )
}
